import React, { useEffect } from "react";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import useTimeState from "hooks/dashboard/useTimeState";
import { ComponentTheme } from "components/fragments/dashboard/components/group-component/GroupComponentConfiguratorNew";
import { dweenColorway } from "utils/constants";
import Plot from "react-plotly.js";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { T } from "services/lang/LanguageService";
import { Layout } from "plotly.js";
import { Instant, ZonedDateTime, ZoneOffset } from "@js-joda/core";
import {
  aggregateValues,
  daysArrayShort,
  defaultFromDateTime,
  defaultToDateTime,
  getLegendGroup,
  getTextColor,
  getXAxisLabel,
  monthsArrayShort,
} from "../charts/utils";
import { PlotDataEx } from "../charts/interfaces";
import {
  AggregatedEntry,
  ChartType,
  ComparisonPeriod,
  DateRangeSelectType,
  PeriodicDataChartProps,
} from "./PeriodicDataChartTypes";
import "./PeriodicDataChart.scss";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";
import { useCompanyTimezone } from "hooks/useTimezone";

const getPeriodicDataChartLayout = (
  sortedXLabels: string[],
  theme?: ComponentTheme,
  reportMode?: boolean
) => {
  return {
    font: {
      color: getTextColor(theme),
    },
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    autosize: true,
    yaxis: {
      tickfont: {
        color: getTextColor(theme),
      },
    },
    xaxis: {
      type: "category",
      categoryorder: "array",
      categoryarray: sortedXLabels,
      tickfont: {
        size: reportMode ? 10 : 14,
        color: getTextColor(theme),
        family: "Arial Narrow, sans-serif",
        weight: 600,
      },
    },
    barmode: "group",
    margin: { t: 30, l: 30, r: 30 },
  };
};

export default function PeriodicDataChart(props: PeriodicDataChartProps) {
  const displayTz = useCompanyTimezone();
  const companyTz = useCompanyTimezone();

  const setStatus = useComponentStateCallback(props);

  const timeState = useTimeState({
    from: props.from ?? props.dateFrom,
    to: props.to ?? props.dateTo,
  });

  const useLoadConfFallback =
    !props.dateRangeSelectType || props.dateRangeSelectType === DateRangeSelectType.LOAD_CONF;

  const defaultFromDate = useLoadConfFallback
    ? defaultFromDateTime({
        zoneId: companyTz,
        loadConf: { ...defaultLoadConf, ...props.loadConf },
        referenceTime: props.referenceTime,
      })
    : timeState.from;

  let defaultToDate = useLoadConfFallback
    ? defaultToDateTime({
        zoneId: companyTz,
        loadConf: {
          ...defaultLoadConf,
          ...props.loadConf,
          alignUnit: props.useDataUpToLastFinishedMonth ? "MONTHS" : props.loadConf?.alignUnit,
          leadUnit: props.useDataUpToLastFinishedMonth ? "MINUTES" : props.loadConf?.leadUnit,
          lead: props.useDataUpToLastFinishedMonth ? 0 : props.loadConf?.lead,
        },
        referenceTime: props.referenceTime,
      })
    : timeState.to;

  if (props.useDataUpToLastFinishedMonth && defaultToDate) {
    const zdt = ZonedDateTime.ofInstant(
      Instant.ofEpochMilli(defaultToDate.getTime()),
      ZoneOffset.UTC
    );
    defaultToDate = new Date(zdt.minusMinutes(2).toInstant().toEpochMilli());
  }

  const {
    data: dataRange,
    isLoading,
    error,
  } = useGetDataInfoRange(
    [props.valueDataset],
    {
      timeStart: defaultFromDate?.toISOString()!,
      timeEnd: defaultToDate?.toISOString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.valueDataset && !!defaultFromDate && !!defaultToDate,
    }
  );

  useEffect(() => {
    if (!isLoading && dataRange !== undefined) {
      setStatus({ state: "finished" });
    }
  }, [isLoading]);

  if (error) {
    easasAlert({
      summary: T("alert.failed-to-fetch-data"),
      detail: error.message,
      severity: "error",
    });
    setStatus({ state: "error", error: error.message });
  }

  const rawData = dataRange?.[0]?.data ?? [];

  const groupedData = new Map<string, Map<string, AggregatedEntry>>();
  const xLabels = new Set<string>();

  rawData.forEach(point => {
    const instant = Instant.parse(point.key);
    let xKey: string;
    if (props.comparisonPeriod === ComparisonPeriod.DAILY_WEEKLY) {
      const zdt = ZonedDateTime.ofInstant(instant, ZoneOffset.UTC);
      xKey = daysArrayShort[zdt.dayOfWeek().ordinal()];
    } else {
      xKey = getXAxisLabel(instant, props.comparisonPeriod, companyTz);
    }
    const legendKey = getLegendGroup(instant, props.comparisonPeriod, companyTz);

    xLabels.add(xKey);

    if (!groupedData.has(legendKey)) groupedData.set(legendKey, new Map());
    const group = groupedData.get(legendKey)!;
    if (!group.has(xKey)) {
      group.set(xKey, {
        total: 0,
        count: 0,
        min: Number.POSITIVE_INFINITY,
        max: Number.NEGATIVE_INFINITY,
      });
    }

    const entry = group.get(xKey)!;
    entry.total += point.value;
    entry.count += 1;
    entry.min = Math.min(entry.min, point.value);
    entry.max = Math.max(entry.max, point.value);
  });

  const getSortedXLabels = () => {
    if (
      props.comparisonPeriod === ComparisonPeriod.MONTHLY_YEARLY ||
      props.comparisonPeriod === undefined
    ) {
      return monthsArrayShort;
    }
    if (props.comparisonPeriod === ComparisonPeriod.DAILY_WEEKLY) {
      return daysArrayShort;
    }
    return Array.from(xLabels).sort();
  };

  const allGroups = Array.from(groupedData.keys()).sort();

  const sortedXLabels = getSortedXLabels();

  const plotData = allGroups.map((groupName, index) => {
    const group = groupedData.get(groupName)!;
    return {
      type: props.chartType ?? ChartType.BAR,
      name: groupName,
      x: sortedXLabels,
      y: sortedXLabels.map(label => {
        const entry = group.get(label);
        return entry
          ? aggregateValues(entry, props.aggregationType)
          : {
              total: 0,
              count: 0,
              min: 0,
              max: 0,
            };
      }),
      marker: {
        color: index === 0 ? getTextColor(props.theme) : (dweenColorway[index - 1] ?? "#ccc"),
      },
    };
  });

  return (
    <div
      style={{
        width: props.reportMode ? props.width : "100%",
        height: props.reportMode ? props.height : "100%",
      }}
      className={`periodic-bar-chart${props.reportMode ? "-report-mode" : ""}`}
    >
      <div className="subtitle" style={{ color: getTextColor(props.theme) }}>
        {props.subtitle}
      </div>
      <div className="title" style={{ color: getTextColor(props.theme) }}>
        {props.title}
      </div>

      <Plot
        data={plotData as PlotDataEx[]}
        useResizeHandler
        className="w-full h-full"
        layout={
          getPeriodicDataChartLayout(
            getSortedXLabels(),
            props.theme,
            props.reportMode
          ) as Partial<Layout>
        }
        config={{ displayModeBar: false }}
      />
    </div>
  );
}
